import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";
import { useTheme } from "@mui/material/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

// Genérico: dado um endpoint de envio, abre seletor de ticket (ou envia direto se ticketId já existe)
// Props:
//   open, onClose
//   title        — ex: "Enviar OS 2026-0001 no chat"
//   subtitle     — ex: "Cliente: João Silva"
//   ticketId     — se já definido, pula seleção e envia direto
//   sendEndpoint — função (ticketId) => string — URL do POST
const SendToChatDialog = ({ open, onClose, title, subtitle, ticketId: initialTicketId, sendEndpoint }) => {
  const theme = useTheme();
  const [ticketId, setTicketId] = useState(initialTicketId || null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketInputValue, setTicketInputValue] = useState(
    initialTicketId ? `#${initialTicketId}` : ""
  );
  const [sending, setSending] = useState(false);
  const timer = useRef(null);

  const handleSearch = (val) => {
    setTicketInputValue(val);
    if (!val || val.length < 2) { setTicketOptions([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setTicketLoading(true);
      try {
        const { data } = await api.get("/tickets", {
          params: { searchParam: val, pageNumber: 1, status: "open" },
        });
        setTicketOptions((data.tickets || []).slice(0, 10));
      } catch { setTicketOptions([]); }
      finally { setTicketLoading(false); }
    }, 350);
  };

  const handleSend = async () => {
    if (!ticketId) { toast.warn("Selecione um ticket."); return; }
    setSending(true);
    try {
      await api.post(sendEndpoint(ticketId));
      toast.success("Enviado no chat!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ paddingBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SendIcon style={{ color: theme.palette.primary.main, fontSize: 20 }} />
          {title}
        </div>
      </DialogTitle>
      <DialogContent>
        {subtitle && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
            {subtitle}
          </Typography>
        )}
        {initialTicketId ? (
          <Typography variant="body2">
            Ticket vinculado: <strong>#{initialTicketId}</strong>
          </Typography>
        ) : (
          <Autocomplete
            freeSolo
            options={ticketOptions}
            inputValue={ticketInputValue}
            onInputChange={(_, val) => handleSearch(val)}
            onChange={(_, ticket) => {
              if (ticket && typeof ticket === "object") {
                setTicketId(ticket.id);
                setTicketInputValue(`#${ticket.id} — ${ticket.contact?.name || ""}`);
              } else {
                setTicketId(null);
              }
            }}
            getOptionLabel={t => typeof t === "string" ? t : `#${t.id} — ${t.contact?.name || ""}`}
            loading={ticketLoading}
            renderInput={params => (
              <TextField
                {...params}
                label="Ticket de destino"
                size="small"
                placeholder="Digite # ou nome do contato..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: ticketLoading
                    ? <CircularProgress size={14} />
                    : params.InputProps.endAdornment,
                }}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions style={{ padding: "8px 16px 14px" }}>
        <Button onClick={onClose} disabled={sending}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !ticketId}
          style={{ backgroundColor: theme.palette.primary.main, color: "#fff" }}
        >
          Enviar no chat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendToChatDialog;
